import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { DashboardService, Dashboard } from 'app/providers/bridge-api/dashboard.service';
import { StorageAPIService, StorageListResult } from 'app/providers/storage-api.service';
import { AlertService } from 'app/services/alert.service';
import { EnableClassTelemetry } from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';
import { SemantriaAccount } from '../bridge-api/account.service';
import {
  RealtimeEventHandler,
  RealtimeService,
  RealtimeMessage,
  RealtimeConnection,
} from '../realtime.service';
import { GroupService } from '../semantria-api/group.service';
import { AccountUtils } from '../../utils/account.utils';
import { AnalysisGroupService, AnalysisGroup } from './analysis-group-service';
import { Analysis, AnalysisService } from './analysis.service';
import { Collection, CollectionName, CollectionService } from './collection.service';
import { ObjectLabel, Sideloadable } from './object-label.service';
import { Upload, UploadService } from './upload.service';

export interface SentimentPolarityRange {
  sentimentPolarity: string;
  min: number;
  max: number;
  color: string;
  value?: number;
}

export interface Project {
  id: string;
  name: string;
  description: string;
  createdAt: Date;
  modifiedAt: Date;
  iconSource: string;
  iconUrl: string;
  groups: AnalysisGroup[];
  collections: Collection[];
  documentCount: number;
  analysesCount: number;
  collectionCount: number;
  dashboards: Dashboard[];
  uploads: Upload[];
  analyses: Analysis[];
  polarities: {
    document: {
      3: SentimentPolarityRange[];
      5: SentimentPolarityRange[];
    };
    feature: {
      3: SentimentPolarityRange[];
      5: SentimentPolarityRange[];
    };
  };
  semantriaGroup: string;
  status: 'DELETION_REQUESTED' | 'DELETION_IN_PROGRESS' | null;
  colorPalette?: string[];
}

export interface ProjectSources {
  collections?: StorageListResult<CollectionName>;
  analysisGroups?: StorageListResult<AnalysisGroup>;
  uploads?: StorageListResult<Upload>;
  analyses?: StorageListResult<Analysis>;
  dashboards?: {
    count: number;
    hits: Dashboard[];
  };
  message?: string;
}

export interface Permission {
  permissionId: string;
  permissionType: string;
  externalId: string;
}
export interface User {
  userId: string;
  login: string;
  description: string;
  projectId: string;
  createdAt: Date;
  modifiedAt: Date;
  accountId: string;
  companyName: string;
  email: string;
  firstName: string;
  lastName: string;
  title: string;
  permissions: Array<Permission>;
  readOnly: boolean;
}

export interface ProjectUsers {
  users: User[];
  admins: User[];
  owner: User;
}
export interface User {
  userId: string;
  login: string;
  description: string;
  projectId: string;
  createdAt: Date;
  modifiedAt: Date;
  accountId: string;
  companyName: string;
  email: string;
  firstName: string;
  lastName: string;
  title: string;
  permissions: Array<Permission>;
  readOnly: boolean;
}

export interface ProjectUsers {
  users: User[];
  admins: User[];
  owner: User;
}

export type ProjectSort = 'auto';
export type ProjectFilter = '';
export const DEFAULT_PROJECT_COLOR_PALETTE = [
  'rgb(124,181,236)',
  'rgb(247,163,92)',
  'rgb(191,255,178)',
  'rgb(119,152,191)',
  'rgb(170,238,238)',
  'rgb(254,153,153)',
  'rgb(238,170,238)',
  'rgb(85,191,59)',
  'rgb(223,83,83)',
];
export const PROJECT_COLOR_LIMIT = 20;
export type ProjectSourcesIncludes =
  | 'analyses'
  | 'dashboards'
  | 'uploads'
  | 'collections'
  | 'analysisGroups';

@EnableClassTelemetry()
@Injectable()
export class ProjectService extends StorageAPIService {
  private projectCache: { [key: string]: Project } = {};

  constructor(
    http: HttpClient,
    systemService: SystemService,
    private groupService: GroupService,
    private analysisService: AnalysisService,
    private alertService: AlertService,
    private realtimeService: RealtimeService,
    private collectionService: CollectionService,
    private accountService: SemantriaAccount,
    private uploadService: UploadService
  ) {
    super(http, systemService);
  }

  static map(item: Project): Project {
    if (item.modifiedAt) {
      item.modifiedAt = new Date(item.modifiedAt);
    }
    if (item.collections) {
      item.collections = item.collections.map((collection) => CollectionService.map(collection));
    } else {
      item.collections = [];
    }
    if (item.groups) {
      item.groups.forEach((group) => {
        AnalysisGroupService.map(group);
      });
    } else {
      item.groups = [];
    }
    return item;
  }

  static mapCollectionName(item: CollectionName, labels?: ObjectLabel[]): CollectionName {
    item.objectLabels = this.mapObjectLabels(item, labels);

    return item;
  }

  on(namespace: string, fn: RealtimeEventHandler<Project>): RealtimeConnection {
    return this.realtimeService.on(
      namespace,
      {
        project: (data: RealtimeMessage<Project>) => {
          ProjectService.map(data.object);
          fn(data);
        },
      },
      'projectService'
    );
  }

  getProjectIds(): Promise<StorageListResult<string[]>> {
    return this.get<StorageListResult<string[]>>('/projects');
  }

  getProjects(detailed = true): Promise<StorageListResult<Project>> {
    return this.searchProjects(null, StorageAPIService.MAX_LIMIT, detailed);
  }

  async searchProjects(
    keyword?: string,
    limit = 20,
    detailed = true,
    all = false,
    onlyExecutive = false,
    group_id = ''
  ): Promise<StorageListResult<Project>> {
    if (!this.groupService.getSelectedGroup()) {
      await this.groupService.refreshAndSelectActiveGroup();
    }
    const groupId = group_id || this.groupService.getSelectedGroupId();
    let queryString = '*';
    if (keyword) {
      queryString = keyword.replace(/[^a-zA-Z0-9_\-\*]/g, '');
      queryString = queryString.indexOf('*') >= 0 ? queryString : `*${queryString}*`;
    }

    const query = `name:${queryString}`;

    const result = await this.getPage<Project>('/projects', {
      detailed,
      query,
      group_id: groupId,
      'page-limit': limit,
      'get-all': all.toString(),
      'executive-only': onlyExecutive.toString(),
    });

    result.items = result.items.map(ProjectService.map);
    return result;
  }

  async getProjectSources(
    projectId: string,
    ...includes: ProjectSourcesIncludes[]
  ): Promise<ProjectSources> {
    const sources = await this.get<Sideloadable<ProjectSources>>(`/projects/${projectId}/sources`, {
      include: includes.join(),
      sideloads: 'objectLabels',
    });

    const hasSocialIntegration = await this.hasSocialIntegration();

    if (sources.collections) {
      if (hasSocialIntegration) {
        // TODO: Add groupId?
        const socialCollections = (await this.collectionService.getSocialCollectionNames(
          projectId
        )) ?? { items: [] };
        sources.collections.items.push(...socialCollections.items);
      }

      sources.collections.items = sources.collections.items.map((c) =>
        ProjectService.mapCollectionName(c, sources.sideloaded?.objectLabels)
      );
    }

    if (sources.analyses) {
      if (hasSocialIntegration) {
        const socialAnalyses = (await this.analysisService.getAllSocialAnalyses(projectId)) ?? [];
        sources.analyses.items.push(...socialAnalyses);
      }

      sources.analyses.items = sources.analyses.items.map((a) =>
        AnalysisService.map(a, sources.sideloaded?.objectLabels)
      );
    }

    if (sources.uploads) {
      if (hasSocialIntegration) {
        const socialUploads = (await this.uploadService.getSocialUploads(projectId)) ?? [];
        sources.uploads.items.push(...socialUploads);
      }
      sources.uploads.items = sources.uploads.items.map(UploadService.mapUpload);
    }

    if (sources.analysisGroups) {
      sources.analysisGroups.items = sources.analysisGroups.items.map((a) =>
        AnalysisGroupService.map(a, sources.sideloaded?.objectLabels)
      );
    }

    if (sources.dashboards) {
      sources.dashboards.hits = sources.dashboards.hits.map((d) =>
        DashboardService.map(d, sources.sideloaded?.objectLabels)
      );
    }

    return sources;
  }

  async getProjectById(projectId: string, force: boolean = true): Promise<Project> {
    if (!force && this.projectCache[projectId]) {
      return this.projectCache[projectId];
    }
    const groupId = this.groupService.getSelectedGroupId();
    const allRequests: any[] = [
      this.get<Project>(`/projects/${projectId}`, { group_id: groupId, sideloads: 'objectLabels' }),
      this.getProjectSources(
        projectId,
        'analyses',
        'dashboards',
        'uploads',
        'collections',
        'analysisGroups'
      ),
    ];

    const hasSocialIntegration = await this.hasSocialIntegration();

    if (hasSocialIntegration) {
      allRequests.push(this.collectionService.getSocialCollections(projectId));
    }

    const results = await Promise.all(allRequests as any);
    const project = results[0] as Project;

    if (hasSocialIntegration) {
      const collections = project.collections ?? [];
      const socialCollections = results[2].items;
      project.collections = [...collections, ...socialCollections];
    }

    const sources = results[1] as ProjectSources;
    const mappedProject = ProjectService.map(project);
    mappedProject.analyses = sources.analyses?.items || [];
    mappedProject.dashboards = sources.dashboards?.hits || [];
    mappedProject.uploads = sources.uploads?.items || [];
    mappedProject.collections?.forEach(
      (c) =>
        (c.objectLabels = sources.collections?.items
          ?.find((sc) => sc.id === c.collectionName)
          ?.objectLabels?.sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { sensitivity: 'accent' })
          ))
    );
    mappedProject.groups = sources.analysisGroups?.items || [];

    this.projectCache[projectId] = mappedProject;
    return mappedProject;
  }

  public touchProject(projectId: string): Promise<void> {
    return this.post<void>(`/projects/${projectId}/_touch`);
  }

  public deleteProject(projectId: string): Promise<void> {
    return this.delete<void>(`/projects/${projectId}`);
  }

  public async createProject(
    name: string,
    description: string,
    iconSource: string = 'standard',
    iconUrl: string = null,
    groupId: string
  ): Promise<Project> {
    const project = await this.post<Project>('/projects/', {
      name,
      description,
      iconSource,
      iconUrl,
      groupId,
    });
    return ProjectService.map(project);
  }

  public async updateProject(id: string, projectData: Partial<Project>): Promise<Project> {
    const project = await this.patch<Project>(`/projects/${id}`, projectData);
    return ProjectService.map(project);
  }

  public async getProjectFields(projectId: string): Promise<any> {
    return this.get<void>(`/projects/${projectId}/_fields`).catch((error) => {
      throw error;
    });
  }

  public deleteCollection(projectId: string, collectionName: string): Promise<void> {
    return this.delete(`/projects/${projectId}/collections/${collectionName}`);
  }

  // TODO: Should this be a util service?
  private async hasSocialIntegration() {
    const account = await this.accountService.sub.latest();
    return AccountUtils.hasFeature(account, 'SOCIAL_INTEGRATION');
  }
}
